import UAParser from 'ua-parser-js';
import URL from 'url-parse';
import * as Cookie from 'tiny-cookie';
import md5 from 'md5';
import _ from 'lodash';
import Localization from './localization';
import utils from './utils.js';
// import links from './links';

const makeConfig = (player) => {
  const userAgentParser = new UAParser();
  let urlParser = new URL(window.location.href, true);
  const localization = new Localization(player);

  const interactive = {
    containerId: 'mggp-interactive-container',
    override: ['button_emoji'],
    get configUrl() {
      return config.interactiveUrl; // || 'https://api-interactive.megogo.net/v1/init';
    },
    get token() {
      return config.jwtToken;
    },
    /**
     * Experimental: instead post message to single iframe broadcast message to all possible acceptor.
     * Motivation: improve DX, it should allow to develop interactive as separated window.
     */
    // useWindow: true,
  };

  const gemius = {
    identifier: 'B7abku_qQIt7f7VYje59IMVHXtGB9uNO0.r_cC0aiLX.Y7',
    prefix: 'sua',
  };

  const comscore = {
    id: 15273939,
  };

  const vigo = {
    id: '11e0',
    timeout: null,
  };

  const kibana = {
    events: {
      http: {
        error: 'http://log.cnt.re:7080/log/html5',
        warning: 'http://log.cnt.re:7080/log/html5',
        player_warnings: 'http://log.cnt.re:7080/log/player_warnings',
        p2p: 'http://log.cnt.re:7080/log/p2p',
      },
      https: {
        error: 'https://log.cnt.re/track/log/html5',
        warning: 'https://log.cnt.re/track/log/html5',
        player_warnings: 'https://log.cnt.re/track/log/player_warnings',
        p2p: 'https://log.cnt.re:7080/log/p2p',
      },
    },
  };

  const config = {
    host: urlParser.hostname,
    href: window.location.href,
    referer: urlParser.query.referer || window.location.href,
    /**
     * JWT Token - required for interactive. Passed from site as part of init arguments.
     */
    jwtToken: '',
    protocol: window.location.protocol,
    userAgent: userAgentParser.getResult(),
    device: userAgentParser.getResult(), // legacy
    localization,
    interactive,
    loadExternalLocalization: false,
    localizationUrl: '',
    skin: urlParser.query.skin || 'default',
    videoURL: '',
    videoID: urlParser.query.video_id || 0,
    supportedDrmType: '',
    debug: urlParser.query.debug === 'true',
    metDebug: false,
    autoPlay:
      urlParser.query.autoplay !== undefined
        ? Boolean(Number(urlParser.query.autoplay))
        : true,
    mutedAutoplay: false,
    muted:
      urlParser.query.muted !== undefined
        ? Boolean(Number(urlParser.query.muted))
        : false,
    volume: urlParser.query.volume ? Number(urlParser.query.volume) : undefined,
    audioTag: urlParser.query.audio ? String(urlParser.query.audio) : undefined,
    audioDRMTag: urlParser.query['audio-dash']
      ? String(urlParser.query['audio-dash'])
      : undefined,
    embed: false,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    userIP: '',
    userID: getUserIdFromHTML(),
    deviceID: '',
    publicKey: 'cms_site',
    timeInterval: 150,
    skipTimeDelay: 1000,
    appVersion: process.env.APP_VERSION,
    buildVersion: process.env.BUILD,
    deviceOS: userAgentParser.getOS().version
      ? `${userAgentParser.getOS().name}_${userAgentParser.getOS().version}`
      : userAgentParser.getOS().name || '',
    deviceModel:
      userAgentParser.getDevice().vendor + userAgentParser.getDevice().model ||
      'undefined',
    deviceType: 'web',
    geo: urlParser.query.lang || 'ua',
    lang: urlParser.query.lang || 'UA',
    lite: false,
    ad: true,
    p2p: false,
    id: null,
    container: document.body || document.createElement('div'),
    storage: null,
    showCloseButton: false,
    singleVideoInstance: false,
    creditsForceStopEnabled: true,
    onReady: null,
    onError: null,
    onSeriesChange: null,
    onGaEvent: null,
    getUserDataWS: () => {
      let userHash = Cookie.get('MGUSERHASH');
      if (!userHash && config.userIP) {
        userHash = md5(config.userIP + window.navigator.userAgent);
        Cookie.set('MGUSERHASH', userHash, { expires: 36500, samesite: 'lax' });
      }

      const userAge = Cookie.get('adcnt_a');
      const userSex = Cookie.get('adcnt_s');

      return {
        deviceId: userHash,
        params: `?age=${userAge}&gender=${userSex}`,
      };
    },
    getURLParser: (url) => new URL(url, true),
    i18n: localization.getText,
    gemius,
    comscore,
    vigo,
    kibana,
    links: {},
    playerOptions: {},
    externalModules: [],
    socials: ['fb', 'twitter', 'livejournal'],
    drm: false,
    espressoMode: false,
    adSpeed: 100,
    lpdid: null,
    vpaidAllow: true, // if false -- skip all next ads in block after vpaid
    m3u8NativSupported: false,
    minBufferLength: 10,
    needDefineGASlot: true,
    gdpr: false,
    gdprConsent: "",
    palNonce: "",
    checkCloseButton: () => {
      const queryCloseButton = _.get(urlParser, 'query.closeButton');
      if (config.showCloseButton && queryCloseButton) {
        config.showCloseButton = queryCloseButton === 'true';
      }
      return config.showCloseButton;
    },
    checkDefaultOptions: (videoInfo) => {
      const options = {};
      const url = new URL(window.location.href, true);

      const quality = config.storage.getQuality();
      const subtitles = config.storage.getSubtitles();
      let audio = player.get('objectAudioId') || config.storage.getLastAudio();
      const volume = (function (savedVolume) {
        const boundary =
          (min = 0, max = 100) =>
          (value) =>
            Math.max(min, Math.min(value, max));
        const getBoundaryVolume = boundary(0, 100);
        const volume = Math.round(parseFloat(savedVolume));
        return Number.isFinite(volume) ? getBoundaryVolume(volume) : 60;
      })(url.query.volume || config.storage.getVolume());
      const muted = config.storage.getMuted();

      urlParser = new URL(window.location.href, true);
      config.audioTag = urlParser.query.audio
        ? String(urlParser.query.audio)
        : undefined;
      config.audioDRMTag = urlParser.query['audio-dash']
        ? String(urlParser.query['audio-dash'])
        : undefined;

        if (
          videoInfo?.audio_tracks &&
          !videoInfo.audio_tracks.some((track) => track.id === audio) // if saved audio is not present in videoInfo or audio is absent
        ) {
        const track = videoInfo.audio_tracks.find((track) => track.is_active);
        if (track) {
          audio = track.id;
        }
      }

      if (quality) {
        options.defaultQuality = quality;
      }
      if (subtitles) {
        options.defaultSubtitles = subtitles;
      } else {
        options.defaultSubtitles = 'off';
      }

      if (audio) {
        options.defaultAudioTrack = audio;
      }
      if (url.query.start) {
        options.startSeconds = url.query.start;
      }

      options.defaultVolume = volume;
      options.defaultMuted = muted || false;

      const playerOptions = utils.addAdditionalPlayerOptions(
        options,
        videoInfo,
        config.storage,
        player
      );

      return playerOptions;
    },
  };

  const osVersion = _.get(config, 'device.os.version', '').split('.');

  if (osVersion.length > 1) {
    config.device.os.major = parseInt(osVersion[0], 10);
    config.device.os.minor = parseInt(osVersion[1], 10);
  }

  // hook for default3 skin
  if (config.skin === 'default3') {
    config.i18nMgg = (id) => config.i18n(id);
  }

  if (urlParser.query.metDebug) {
    Cookie.set('metDebug', urlParser.query.metDebug, { samesite: 'lax' });
  }

  config.metDebug = Cookie.get('metDebug') === 'true';

  const device = _.get(config, 'userAgent.device', {});
  if (device.type === 'mobile' || device.type === 'tablet') {
    config.m3u8NativSupported = true;
    config.singleVideoInstance = true;
  }

  if ('as' in urlParser.query) {
    try {
      config.adSpeed = Number(urlParser.query.as);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error.message);
    }
  }
  if ('va' in urlParser.query) {
    try {
      config.vpaidAllow = Boolean(Number(urlParser.query.va));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error.message);
    }
  }
  if ('em' in urlParser.query) {
    try {
      config.espressoMode = Boolean(Number(urlParser.query.em));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error.message);
    }
  }

  // eslint-disable-next-line no-console
  console.log(`Player loader version: ${config.appVersion}`);
  // eslint-disable-next-line no-console
  console.log(`Player build version: ${config.buildVersion}`);

  config.links = require('./loaderConfig.json');
  return config;
};

function getUserIdFromHTML() {
  let result = ''; // empty string or number
  const section = document.querySelector('[data-user-id]');
  if (section) {
    const userId = section.getAttribute('data-user-id');
    if (userId) {
      result = parseInt(userId, 10);
    }
  }
  return result;
}

export default makeConfig;
